/**
 * Field Processing Utilities
 * Handles field value processing, text transformations, and categorization
 */

import { ExportTemplate } from '../../../types/exportTemplates';
import { stripHtmlTags } from '../core/formatting';

/**
 * Apply text case transformation to a string
 * @param text - The text to transform
 * @param textCase - The text case to apply: 'uppercase', 'lowercase', or 'original'
 */
export function applyTextCase(text: string, textCase?: 'uppercase' | 'lowercase' | 'original'): string {
  if (!text || typeof text !== 'string') return text;
  
  switch (textCase) {
    case 'uppercase':
      return text.toUpperCase();
    case 'lowercase':
      return text.toLowerCase();
    case 'original':
    default:
      return text;
  }
}

/**
 * Parse text case from export behavior string (e.g., 'value:uppercase' -> 'uppercase')
 */
export function parseTextCaseFromExportBehavior(exportBehavior?: string): 'uppercase' | 'lowercase' | 'original' {
  if (!exportBehavior || !exportBehavior.includes(':')) return 'original';
  const textCase = exportBehavior.split(':')[1];
  if (textCase === 'uppercase' || textCase === 'lowercase') return textCase;
  return 'original';
}

/**
 * Process field value based on export options
 * @param value - The field value to process
 * @param fieldType - The field type (e.g., 'text', 'textarea', 'title')
 * @param options - Global export options
 * @param fieldExportBehavior - Per-field export behavior (e.g., 'value:uppercase')
 */
export function processFieldValue(
  value: any,
  fieldType: string,
  options?: ExportTemplate['options'],
  fieldExportBehavior?: string
): any {
  let processedValue = value;
  
  // Strip HTML from text fields if option is enabled (default: true)
  if (options?.stripHtml !== false) {
    if (fieldType === 'richtext' || fieldType === 'textarea' || fieldType === 'text') {
      if (typeof processedValue === 'string') {
        processedValue = stripHtmlTags(processedValue);
      }
    }
  }
  
  // Apply text case transformation for text fields based on per-field export behavior
  if (fieldType === 'richtext' || fieldType === 'textarea' || fieldType === 'text' || fieldType === 'title') {
    if (typeof processedValue === 'string') {
      const textCase = parseTextCaseFromExportBehavior(fieldExportBehavior);
      processedValue = applyTextCase(processedValue, textCase);
    }
  }
  
  return processedValue;
}

/**
 * Group items by category based on their template/blueprint
 */
export function groupItemsByCategory(
  items: any[],
  blueprints: any[],
  categoryMapping?: Record<string, string>
): Array<{ name: string; items: any[]; templateName: string }> {
  const categories: Map<string, { name: string; items: any[]; templateName: string }> = new Map();

  items.forEach(item => {
    // Find the blueprint for this item
    const blueprint = blueprints.find(bp => bp.id === item.blueprint_id);
    const templateName = blueprint?.name || 'Unknown';

    // If categoryMapping is provided, ONLY include items whose template is in the mapping
    // This ensures we only export items that have been configured in DatabaseSection
    if (categoryMapping && Object.keys(categoryMapping).length > 0) {
      if (!categoryMapping[templateName]) {
        // Skip items whose template is not in the category mapping
        return;
      }
    }

    // Determine category name from mapping or use template name
    let categoryName = templateName;
    if (categoryMapping && categoryMapping[templateName]) {
      categoryName = categoryMapping[templateName];
    } else {
      // Default: add 's' to make it plural if not already
      categoryName = templateName.endsWith('s') ? templateName : `${templateName}s`;
    }

    // Get or create category
    if (!categories.has(categoryName)) {
      categories.set(categoryName, {
        name: categoryName,
        items: [],
        templateName: templateName
      });
    }

    // Add item to category
    categories.get(categoryName)!.items.push(item);
  });

  return Array.from(categories.values());
}
